/**
 * =============================================================================
 * Hook: useWorkspacePlaybookBindings
 * Versão: 1.0.0
 *
 * Descrição:
 *   Gerencia regras de disparo automático (bindings) de Playbooks de
 *   Atividades por workspace/pipeline/fase. Cada binding define quando
 *   uma cadência deve ser iniciada para um deal.
 *
 *   Padrão server-side abstraction: TODAS as operações passam por RPCs
 *   `SECURITY DEFINER` com p_tenant_hint. Nenhum acesso direto às tabelas.
 *
 * Release notes:
 *   v1.0.0 - Criação (Fase 1, item 1.14 do Playbook de Vendas).
 * =============================================================================
 */
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { createLogger } from '@/lib/logger';

const logger = createLogger('useWorkspacePlaybookBindings');

// ----------------------------------------------------------------------------
// Tipos
// ----------------------------------------------------------------------------
export type CadenceTrigger = 'deal_created' | 'deal_enters_phase' | 'manual';

export interface PlaybookBinding {
  id: string;
  workspace_id: string;
  pipeline_id: string | null;
  phase_id: string | null;
  playbook_id: string;
  playbook_name: string;
  trigger: CadenceTrigger;
  cancel_previous: boolean;
  lead_source_filter: string[] | null;
  is_active: boolean;
  sort_order: number;
  created_at: string;
  updated_at: string;
}

export interface UpsertBindingInput {
  id?: string | null;
  workspace_id: string;
  pipeline_id?: string | null;
  phase_id?: string | null;
  playbook_id: string;
  trigger: CadenceTrigger;
  cancel_previous?: boolean;
  lead_source_filter?: string[] | null;
  is_active?: boolean;
  sort_order?: number;
}

// ----------------------------------------------------------------------------
// Hook principal
// ----------------------------------------------------------------------------
export function useWorkspacePlaybookBindings(workspaceId: string | null | undefined) {
  const { currentTenant } = useAuth();
  const queryClient = useQueryClient();
  const tenantId = currentTenant?.id ?? null;

  // Lista de bindings do workspace
  const listQuery = useQuery({
    queryKey: ['playbook-bindings', tenantId, workspaceId],
    enabled: !!tenantId && !!workspaceId,
    queryFn: async (): Promise<PlaybookBinding[]> => {
      const { data, error } = await supabase.rpc('list_pipeline_playbook_bindings', {
        p_workspace_id: workspaceId!,
        p_pipeline_id: null,
        p_tenant_hint: tenantId,
      });
      if (error) {
        logger.error('Erro ao listar bindings', error);
        throw error;
      }
      return (data ?? []) as PlaybookBinding[];
    },
  });

  // Upsert (criar/editar regra)
  const upsertMutation = useMutation({
    mutationFn: async (payload: UpsertBindingInput) => {
      const { data, error } = await supabase.rpc('upsert_pipeline_playbook_binding', {
        p_payload: payload as any,
        p_tenant_hint: tenantId,
      });
      if (error) throw error;
      return data as string;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['playbook-bindings', tenantId, workspaceId] });
      queryClient.invalidateQueries({ queryKey: ['activity-playbooks', tenantId] });
    },
  });

  // Delete
  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.rpc('delete_pipeline_playbook_binding', {
        p_id: id,
        p_tenant_hint: tenantId,
      });
      if (error) throw error;
      return true;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['playbook-bindings', tenantId, workspaceId] });
      queryClient.invalidateQueries({ queryKey: ['activity-playbooks', tenantId] });
    },
  });

  return {
    bindings: listQuery.data ?? [],
    isLoading: listQuery.isLoading,
    refetch: listQuery.refetch,
    upsertBinding: upsertMutation.mutateAsync,
    isUpserting: upsertMutation.isPending,
    deleteBinding: deleteMutation.mutateAsync,
    isDeleting: deleteMutation.isPending,
  };
}
